import { Check, FilterList } from '@mui/icons-material';
import { Chip, ListItemIcon, ListItemText, MenuItem, MenuList, Popover, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';

import { splitOptionCount } from '@sophocles/components/FilterBar/filterOptionLabel';
import {
  NoOptions,
  OPTION_LIST_MAX_WIDTH,
  OptionCount,
  OptionLabel,
} from '@sophocles/data-grids/components/filters/FilterOptionRow';
import { toolbarChipSx } from '@sophocles/data-grids/components/toolbar';
import { OptionMark } from '@sophocles/data-grids/grid/descriptorIcons';

function getDefaultLabel(option) {
  return option?.label ?? option?.value ?? '';
}

export default function DataGridSingleSelectFilter({
  label,
  value = null,
  onChange,
  options = [],
  allLabel = null,
  disabled = false,
  chipLabel = null,
  popoverTitle = null,
  getOptionLabel = getDefaultLabel,
  // (option) => ReactNode — custom rendering for menu items AND the
  // filled trigger chip. Defaults to plain text via `getOptionLabel`.
  renderOption = null,
  icon: iconProp,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleOpen = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    nextValue => {
      onChange(nextValue);
      handleClose();
    },
    [handleClose, onChange],
  );

  const activeOption = useMemo(() => options.find(option => option.value === value) ?? null, [options, value]);

  // Chip is always ``Field: State`` so it reads as a complete sentence
  // regardless of whether a value is set:
  //   - inactive: "Has Radios: Any"  (outlined)
  //   - active:   "Has Radios: Yes"  (filled)
  // Wording like "Has Radios" alone is ambiguous — it can read as either
  // a field name or a current filter assertion. The two-part format
  // removes that ambiguity and lets the outlined-vs-filled chip variant
  // carry the active/inactive signal independently.
  const resolvedAllLabel = allLabel ?? 'All';
  // Trigger chip stays plain text ("Field: State") even when renderOption
  // is set — the option's custom rendering is reserved for the dropdown
  // menu items so the toolbar chip doesn't nest chips inside chips.
  const resolvedChipLabel = (() => {
    if (chipLabel) return chipLabel;
    const state = activeOption ? getOptionLabel(activeOption) : resolvedAllLabel;
    return label ? `${label}: ${state}` : state;
  })();
  const resolvedPopoverTitle = popoverTitle ?? label;
  const renderItemContent = option => (renderOption ? renderOption(option) : getOptionLabel(option));

  return (
    <>
      <Chip
        icon={iconProp || <FilterList sx={{ fontSize: 16 }} />}
        label={resolvedChipLabel}
        title={typeof resolvedChipLabel === 'string' ? resolvedChipLabel : undefined}
        size="small"
        variant={value != null ? 'filled' : 'outlined'}
        disabled={disabled}
        onClick={handleOpen}
        sx={toolbarChipSx}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 220, maxWidth: OPTION_LIST_MAX_WIDTH, maxHeight: 400 } } }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary' }}>
          {resolvedPopoverTitle}
        </Typography>
        <MenuList dense>
          <MenuItem onClick={() => handleSelect(null)}>
            <ListItemIcon sx={{ minWidth: 28 }}>{value == null ? <Check fontSize="small" /> : null}</ListItemIcon>
            <ListItemText>{resolvedAllLabel}</ListItemText>
          </MenuItem>
          {options.map(option => {
            const { label, count } = splitOptionCount({ ...option, label: renderItemContent(option) });
            return (
              <MenuItem key={option.value} disabled={option.disabled} onClick={() => handleSelect(option.value)}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {value === option.value ? <Check fontSize="small" /> : null}
                </ListItemIcon>
                <OptionMark icon={option.icon} tone={option.tone} />
                <OptionLabel label={label} />
                <OptionCount count={count} />
              </MenuItem>
            );
          })}
        </MenuList>
        {options.length === 0 && <NoOptions label={label} />}
      </Popover>
    </>
  );
}
